import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { mensagemDoErro } from '../api';
import {
  Aviso, Campo, Carregando, ColunaOrdenavel, ErroDaApi, JanelaDocumento,
  Selo, Vazio, ordenarPor, useOrdenacao,
} from '../ui';
import type { Tom } from '../ui';
import { useSessao } from '../sessao';
import { useCaminho } from '../Caminho';
import { Seletor, SeletorDeCliente, type OpcaoDeFiltro } from '../Filtros';
import {
  BarraDeLista, BuscaDaBarra, FiltrosAtivos, type FiltroAtivo,
} from '../BarraDeLista';
import { Periodo } from '../Periodo';
import { NomeDaAmostra, daApi } from '../NomeDaAmostra';
import {
  aprovarLote, filaDeAssinatura, historicoDeAssinatura, laudoParaConferir,
  osParaAssinar, pessoasQueAssinaram, verificarLote,
  type EnsaioAssinado, type EnsaioParaAssinar, type OsParaAssinar,
  type PessoaQueAssinou,
} from '../api/assinatura';

/**
 * Verificação e aprovação.
 *
 * ── O que a Testesolo pediu ─────────────────────────────────────────
 *
 * «Não encontrei o caminho para aprovar um ensaio. Talvez seja melhor
 *  criar um item separado, à parte da fila do laboratório, onde os
 *  relatórios preenchidos e concluídos vão para verificação e aprovação
 *  final. A verificação e aprovação deve ser por grupos de amostras de
 *  uma mesma OS. Facilita, até para entender o comportamento geotécnico
 *  de determinado local.» — 02/09/2026.
 *
 * ── Por que é uma tela separada da Fila ─────────────────────────────
 *
 * São dois públicos. A Fila do laboratório é de quem LANÇA: ela mostra o
 * que falta fazer. Esta é de quem CONFERE e ASSINA: ela mostra o que
 * está pronto e parado esperando um nome. Misturar as duas foi o que fez
 * a etapa de aprovação ficar invisível — quem aprova tinha de varrer a
 * fila procurando.
 *
 * ── Por que agrupada por OS, e em ordem de profundidade ─────────────
 *
 * Porque é a coluna de solo que se está lendo. Verificar um ensaio
 * isolado é o que impede notar que o limite de liquidez de 1,5 m destoa
 * dos vizinhos — e quem assina responde tecnicamente por essa leitura,
 * não por uma linha dela.
 *
 * ── O que esta tela NÃO decide ──────────────────────────────────────
 *
 * **Se dá para assinar a OS pela metade.** Dá: seis prontos de oito são
 * seis prontos. O cabeçalho diz quantos ainda estão no laboratório, para
 * a decisão ser tomada sabendo.
 *
 * **Quem pode assinar o quê.** Quem recusa é o banco: `RN-124` (quem
 * executou não confere nem aprova) e `RN-121` (não se aprova sem versão
 * de cálculo congelada). A tela pergunta antes e mostra o impedimento na
 * linha, para o botão não prometer o que a trava vai negar.
 */

const TOM_ETAPA: Record<string, Tom> = {
  aguardando_verificacao: 'atencao',
  aguardando_aprovacao: 'neutro',
};

const ROTULO_ETAPA: Record<string, string> = {
  aguardando_verificacao: 'A verificar',
  aguardando_aprovacao: 'A aprovar',
};

const prof = (a: string | null, b: string | null) => {
  if (a == null && b == null) return null;
  const n = (v: string | null) =>
    v == null ? '?' : Number(v).toLocaleString('pt-BR',
      { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n(a)} – ${n(b)} m`;
};

const dia = (v: string | null) =>
  v ? String(v).slice(0, 10).split('-').reverse().join('/') : '—';

/**
 * «Lançado há X dias» — em palavras, não em número solto.
 *
 * «Nos que ainda não foram aprovados também coloque "lançado há X
 *  dias"» — Testesolo, 02/09/2026.
 *
 * A frase importa: «14» numa coluna chamada «lançado» é ambíguo (dia 14?
 * catorze dias?), e é exatamente o tipo de ambiguidade que faz alguém ler
 * o número errado numa lista de trinta linhas.
 */
const haDias = (d: number | null) => {
  if (d == null) return '—';
  if (d <= 0) return 'hoje';
  if (d === 1) return 'há 1 dia';
  return `há ${d} dias`;
};

/**
 * O tom da espera.
 *
 * As faixas não vêm de norma nenhuma — são de leitura: uma semana parado
 * ainda é rotina; um mês parado é uma pergunta. Por isso a cor não
 * decide nada e não bloqueia nada, só chama o olho. Se a Testesolo
 * quiser prazo de conferência como REGRA, ela vira trava no banco, e não
 * uma cor na tela.
 */
const tomDaEspera = (d: number | null): Tom | null => {
  if (d == null) return null;
  if (d >= 30) return 'critico';
  if (d >= 7) return 'atencao';
  return null;
};

// ── a lista de OS ────────────────────────────────────────────────────

/*
 * A situação da OS na fila (Testesolo, 02/09/2026: filtros em lista
 * suspensa). A fila só tem duas perguntas — «tem o que verificar?» e
 * «tem o que aprovar?» — e uma OS pode responder sim às duas.
 */
const SITUACOES_DA_FILA: OpcaoDeFiltro[] = [
  { valor: 'verificar', texto: 'Com ensaio a verificar' },
  { valor: 'aprovar', texto: 'Com ensaio a aprovar' },
  { valor: 'travados', texto: 'Com ensaio travado' },
];

export function Assinatura() {
  const [linhas, setLinhas] = useState<OsParaAssinar[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [situacao, setSituacao] = useState<string | null>(null);
  const [cliente, setCliente] = useState<string | null>(null);
  /*
   * A fila vem inteira da API — dezenas de linhas, não milhares — e por
   * isso ordena na memória. O arquivo, que é cortado em 500, ordena no
   * servidor: ordenar só a página visível mentiria sobre o resto.
   */
  const { ordem, sentido, trocar } = useOrdenacao('col_parada',
    { descendentes: ['col_parada', 'col_verificar', 'col_aprovar',
      'col_amostras', 'col_laboratorio'] });

  useEffect(() => {
    filaDeAssinatura({ cliente }).then(setLinhas).catch(setErro);
  }, [cliente]);

  const emOrdem = useMemo(() => {
    if (!linhas) return null;
    /* A situação filtra na memória: a fila vem inteira, e a pergunta é
       sobre os números que ela já traz. */
    const vistas = linhas.filter((l) => (
      situacao === 'verificar' ? Number(l.a_verificar) > 0
        : situacao === 'aprovar' ? Number(l.a_aprovar) > 0
          : situacao === 'travados' ? Number(l.travados) > 0
            : true));
    const chave: Record<string, (l: OsParaAssinar) => string | number | null> = {
      col_os: (l) => l.os_numero,
      col_cliente: (l) => l.cliente ?? l.obra_nome,
      col_amostras: (l) => l.amostras,
      col_verificar: (l) => l.a_verificar,
      col_aprovar: (l) => l.a_aprovar,
      col_parada: (l) => l.parada_ha,
      col_laboratorio: (l) => l.ainda_no_laboratorio,
    };
    return ordenarPor(vistas, chave[ordem] ?? chave.col_parada, sentido);
  }, [linhas, ordem, sentido, situacao]);

  if (erro) return <ErroDaApi erro={erro} />;
  if (!emOrdem) return <Carregando texto="Procurando o que espera assinatura" />;

  /*
   * `Number` de propósito: `count(*)` do Postgres chega como TEXTO no
   * driver (bigint não cabe em double sem perda), e `0 + '2'` é `'02'`.
   * O cabeçalho dizia «02 ensaio(s)» e a fotografia da tela é que
   * mostrou.
   */
  const total = emOrdem.reduce((s, l) => s + Number(l.ensaios), 0);

  return (
    <div>
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Verificação e aprovação</h1>
          <p className="sub">
            O que está pronto e parado esperando um nome — por ordem de
            serviço, o que espera há mais tempo primeiro
          </p>
        </div>
      </div>

      {/*
        * A FILA, em cima e separada.
        *
        * «Os ensaios que ainda não foram verificados ficam no topo
        *  separados, aguardando o responsável. Os que já foram aprovados
        *  vão para baixo» — Testesolo, 02/09/2026.
        *
        * Duas tabelas e não uma com filtro de situação, porque as duas
        * perguntas se ordenam ao contrário: a fila quer o mais PARADO no
        * topo, o arquivo quer o mais RECENTE.
        */}
      <h2 className="secao">Esperando assinatura</h2>

      <BarraDeLista rotulo="Filtros da fila de assinatura">
        <Seletor naBarra rotulo="Situação" valor={situacao} opcoes={SITUACOES_DA_FILA}
          aoMudar={setSituacao} todos="Tudo o que espera" />
        <SeletorDeCliente naBarra valor={cliente} aoMudar={setCliente} />
      </BarraDeLista>
      <FiltrosAtivos
        itens={[
          ...(situacao ? [{ chave: 'sit', texto: `Situação: ${SITUACOES_DA_FILA.find((o) => o.valor === situacao)?.texto ?? situacao}`, tirar: () => setSituacao(null) }] : []),
          ...(cliente ? [{ chave: 'cli', texto: 'Cliente escolhido', tirar: () => setCliente(null) }] : []),
        ]}
        aoLimpar={() => { setSituacao(null); setCliente(null); }}
        contagem={`${total} ${total === 1 ? 'ensaio' : 'ensaios'} em ${emOrdem.length} OS · ordem: parada há ↓`} />

      {emOrdem.length === 0 ? (
        <Vazio>
          Nada esperando assinatura. Os ensaios chegam aqui quando a ficha
          fecha e os cálculos são processados.
        </Vazio>
      ) : (
        <>
          <p className="sub">
            {total} ensaio(s) em {emOrdem.length} ordem(ns) de serviço.
          </p>
          {/* A tabela rola dentro do quadro; a página nunca anda de lado
              (UX-069: no celular a fila abria com 844px de largura). */}
          <div className="rola"><table className="dados">
            <thead>
              <tr>
                <ColunaOrdenavel campo="col_os" texto="OS"
                  ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                <ColunaOrdenavel campo="col_cliente" texto="Cliente / obra"
                  ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                <ColunaOrdenavel campo="col_amostras" texto="Amostras"
                  classe="num"
                  ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                <ColunaOrdenavel campo="col_verificar" texto="A verificar"
                  classe="num"
                  ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                <ColunaOrdenavel campo="col_aprovar" texto="A aprovar"
                  classe="num"
                  ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                <ColunaOrdenavel campo="col_parada" texto="Parada há"
                  classe="num"
                  ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                <ColunaOrdenavel campo="col_laboratorio"
                  texto="Ainda no laboratório"
                  ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                <th />
              </tr>
            </thead>
            <tbody>
              {emOrdem.map((l) => (
                <tr key={l.os_id}>
                  <td><strong>{l.os_numero}</strong></td>
                  <td>
                    {l.cliente ?? '—'}
                    <small className="det linha-de-baixo">
                      {l.obra_nome ?? '—'}
                    </small>
                  </td>
                  <td className="num">{l.amostras}</td>
                  <td className="num">
                    {l.a_verificar > 0
                      ? <Selo tom="atencao">{l.a_verificar}</Selo> : '—'}
                  </td>
                  <td className="num">
                    {l.a_aprovar > 0
                      ? <Selo tom="neutro">{l.a_aprovar}</Selo> : '—'}
                  </td>
                  {/*
                    * O ensaio mais ANTIGO parado, e não a média: a média
                    * esconde a amostra esquecida há três semanas numa OS
                    * que anda.
                    */}
                  <td className="num">
                    {l.parada_ha == null ? '—' : `${l.parada_ha} d`}
                  </td>
                  <td>
                    {l.ainda_no_laboratorio > 0
                      ? `${l.ainda_no_laboratorio} não chegaram`
                      : <span className="nada">nenhum</span>}
                  </td>
                  <td>
                    <Link to={`/assinatura/${encodeURIComponent(l.os_numero)}`}
                          className="como-link">
                      Abrir
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </>
      )}

      <HistoricoDeAssinaturas />
    </div>
  );
}

// ── o arquivo: o que já foi assinado ─────────────────────────────────

/**
 * O histórico de verificações e aprovações.
 *
 * «Não seria bom deixar o histórico de verificações e aprovações? [...]
 *  com um filtro para localizar a verificação e aprovação, com a data
 *  que ocorreu cada atividade.» — Testesolo, 02/09/2026.
 *
 * ── Por que fica na MESMA tela, embaixo ───────────────────────────────
 *
 * Porque é a mesma pergunta em dois tempos. Quem abre esta tela ou vem
 * assinar, ou vem conferir o que foi assinado — e a segunda visita quase
 * sempre começa na primeira («eu aprovei isso ou não?»). Numa tela à
 * parte, a resposta ficaria a dois cliques de onde a dúvida nasce.
 *
 * ── Por que não carrega junto com a fila ──────────────────────────────
 *
 * Porque o arquivo só cresce. Carregá-lo na abertura faria a fila —
 * que é o trabalho de hoje — esperar por três anos de histórico. Ele
 * abre quando se pede, e aí sim busca.
 */
function HistoricoDeAssinaturas() {
  const [aberto, setAberto] = useState(false);
  const [linhas, setLinhas] = useState<EnsaioAssinado[] | null>(null);
  const [pessoas, setPessoas] = useState<PessoaQueAssinou[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [busca, setBusca] = useState('');
  const [pessoa, setPessoa] = useState('');
  const [cliente, setCliente] = useState<string | null>(null);
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  /*
   * A ordenação é do SERVIDOR aqui: a consulta corta em 500 linhas, e
   * ordenar só o que veio mentiria sobre o que ficou de fora.
   */
  const { ordem, sentido, trocar } = useOrdenacao('col_aprovacao',
    { descendentes: ['col_aprovacao', 'col_verificacao', 'col_lancado',
      'col_espera'] });

  useEffect(() => {
    if (!aberto) return;
    let vivo = true;
    setErro(null);
    historicoDeAssinatura({ busca, pessoa, de, ate, ordem, sentido, cliente })
      .then((l) => { if (vivo) setLinhas(l); })
      .catch((e) => { if (vivo) setErro(e); });
    return () => { vivo = false; };
  }, [aberto, busca, pessoa, cliente, de, ate, ordem, sentido]);

  useEffect(() => {
    if (!aberto || pessoas.length) return;
    pessoasQueAssinaram().then(setPessoas).catch(() => { /* o filtro
      de pessoa é conveniência; sem ele a busca livre continua. */ });
  }, [aberto, pessoas.length]);

  const filtrando = Boolean(busca || pessoa || cliente || de || ate);

  return (
    <section className="historico-assinaturas">
      <h2 className="secao">
        <button type="button" className="como-link"
          onClick={() => setAberto((a) => !a)}
          aria-expanded={aberto}>
          {/*
            * A seta de abrir/fechar é do CSS, e não do texto.
            *
            * Um caractere decorativo dentro do rótulo entra no
            * `innerText` do botão — e aí o nome do comando deixa de ser
            * «Histórico de verificações e aprovações» e passa a ser
            * «▸ Histórico…», tanto para quem lê a tela com leitor
            * quanto para quem procura o botão pelo nome.
            */}
          Histórico de verificações e aprovações
        </button>
      </h2>

      {!aberto ? (
        <p className="sub">
          O que já foi conferido e assinado, com a data de cada atividade
          — abre quando você pedir, para a fila de cima não esperar pelo
          arquivo.
        </p>
      ) : (
        <>
          {/*
            * A barra do padrão (onda 273): os dois campos «Aprovado de /
            * até» viraram o Período com um calendário só, e o menu «Quem
            * assinou» é o mesmo menu da barra de todas as listas.
            */}
          <BarraDeLista rotulo="Filtros do histórico">
            <BuscaDaBarra valor={busca} aoMudar={setBusca}
              placeholder="OS, amostra, ensaio, cliente ou pessoa" rotulo="Procurar no histórico" />
            <SeletorDeCliente naBarra valor={cliente} aoMudar={setCliente} />
            <Seletor naBarra rotulo="Quem assinou" valor={pessoa || null}
              opcoes={pessoas.map((x) => ({ valor: x.id, texto: `${x.nome} (${x.ensaios})` }))}
              aoMudar={(v) => setPessoa(v ?? '')} todos="Qualquer pessoa" />
            <Periodo valor={{ de, ate }} rotulo="Aprovado"
              aoMudar={(v) => { setDe(v.de); setAte(v.ate); }}
              atalhos={['semana', 'mes', 'mesPassado', 'ultimos30', 'tudo']} />
          </BarraDeLista>
          <FiltrosAtivos
            itens={[
              ...(busca ? [{ chave: 'q', texto: `Busca: ${busca}`, tirar: () => setBusca('') }] : []),
              ...(cliente ? [{ chave: 'cli', texto: 'Cliente escolhido', tirar: () => setCliente(null) }] : []),
              ...(pessoa ? [{ chave: 'p', texto: `Assinou: ${pessoas.find((x) => x.id === pessoa)?.nome ?? ''}`, tirar: () => setPessoa('') }] : []),
              ...((de || ate) ? [{ chave: 'per', texto: `Aprovado: ${de || '…'} – ${ate || '…'}`.replace(/(\d{4})-(\d{2})-(\d{2})/g, '$3/$2/$1'), tirar: () => { setDe(''); setAte(''); } }] : []),
            ] as FiltroAtivo[]}
            aoLimpar={() => { setBusca(''); setPessoa(''); setCliente(null); setDe(''); setAte(''); }}
            contagem={linhas ? `${linhas.length} ${linhas.length === 1 ? 'assinatura' : 'assinaturas'}` : undefined} />

          {erro && <ErroDaApi erro={erro} />}
          {!linhas && !erro
            && <Carregando texto="Abrindo o arquivo de assinaturas" />}

          {linhas && linhas.length === 0 && (
            <Vazio>
              {filtrando
                ? 'Nenhuma assinatura com esse filtro.'
                : 'Nenhum ensaio aprovado ainda. O arquivo enche à medida '
                  + 'que a fila de cima se esvazia.'}
            </Vazio>
          )}

          {linhas && linhas.length > 0 && (
            <>
              <p className="sub">
                {linhas.length} ensaio(s) aprovado(s) — «espera» é do
                lançamento à aprovação, em dias
                {linhas.length === 500 && ' — mostrando os 500 primeiros; '
                  + 'estreite o período para ver o resto'}
                .
              </p>
              <div className="rolagem-lateral">
                <table className="dados">
                  <thead>
                    <tr>
                      <ColunaOrdenavel campo="col_os" texto="OS"
                        ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                      <ColunaOrdenavel campo="col_cliente"
                        texto="Cliente / obra" opcional
                        ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                      <ColunaOrdenavel campo="col_amostra" texto="Amostra"
                        ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                      <ColunaOrdenavel campo="col_profundidade"
                        texto="Profundidade" opcional
                        ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                      <ColunaOrdenavel campo="col_metodo" texto="Ensaio"
                        ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                      <ColunaOrdenavel campo="col_lancado" texto="Lançado"
                        ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                      <ColunaOrdenavel campo="col_verificador"
                        texto="Verificado por"
                        ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                      {/*
                        * «Verificado em», e não «Em».
                        *
                        * A tabela rola de lado, e um cabeçalho que só
                        * significa alguma coisa ao lado do vizinho perde
                        * o significado assim que o vizinho sai da tela —
                        * que é exatamente o que acontece na décima
                        * coluna de onze.
                        */}
                      <ColunaOrdenavel campo="col_verificacao"
                        texto="Verificado em" ordem={ordem} sentido={sentido}
                        aoTrocar={trocar} />
                      <ColunaOrdenavel campo="col_aprovador"
                        texto="Aprovado por"
                        ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                      <ColunaOrdenavel campo="col_aprovacao"
                        texto="Aprovado em" ordem={ordem} sentido={sentido}
                        aoTrocar={trocar} />
                      <ColunaOrdenavel campo="col_espera"
                        texto="Espera" classe="num"
                        ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((l) => (
                      <tr key={l.ensaio_id}>
                        <td>
                          <Link
                            to={`/os/${l.os_id}`}
                            className="como-link">{l.os_numero}</Link>
                        </td>
                        <td className="col-opcional">
                          {l.cliente ?? '—'}
                          <small className="det linha-de-baixo">
                            {l.obra_nome ?? '—'}
                          </small>
                        </td>
                        <td>
                          <Link to={`/amostras/${l.amostra_id}`}
                            className="como-link">
                            {/* 303 (RN-248): o nome do cliente, o código e
                                a OS embaixo — a lista mistura OS. */}
                            <NomeDaAmostra amostra={daApi(l, true)} />
                          </Link>
                        </td>
                        <td className="col-opcional">
                          {prof(l.profundidade_inicial, l.profundidade_final)
                            ?? '—'}
                        </td>
                        <td>
                          {l.metodo_codigo}
                          <small className="det linha-de-baixo">
                            {l.metodo_nome}
                          </small>
                        </td>
                        <td>{dia(l.lancado_em)}</td>
                        <td>{l.verificado_por_nome ?? '—'}</td>
                        <td>{dia(l.data_verificacao)}</td>
                        <td>
                          {l.aprovado_por_nome ?? '—'}
                          {/*
                            * A marca de que UM nome fez as duas pontas.
                            * O banco permite (só recusa que o EXECUTOR
                            * confira ou aprove — `RN-124`); apertar isso
                            * é decisão da Testesolo. Enquanto for
                            * permitido, fica visível — o §15 pede
                            * segregação de funções, e o que não se vê
                            * não se audita.
                            */}
                          {l.mesmo_nome_conferiu_e_aprovou && (
                            <small className="det linha-de-baixo"
                              title="A mesma pessoa conferiu e aprovou">
                              conferiu e aprovou
                            </small>
                          )}
                        </td>
                        <td>{dia(l.data_aprovacao)}</td>
                        <td className="num">
                          {l.dias_do_lancamento_a_aprovacao == null
                            ? '—'
                            : `${l.dias_do_lancamento_a_aprovacao} d`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}

// ── uma OS, com o perfil ─────────────────────────────────────────────

export function AssinaturaDaOs() {
  const { numero = '' } = useParams();
  const { eu, pode } = useSessao();
  const navegar = useNavigate();
  const [dados, setDados] = useState<
    { os: OsParaAssinar; ensaios: EnsaioParaAssinar[];
      tudo_assinado?: boolean } | null>(null);
  useCaminho(dados ? [{ rotulo: 'OS', codigo: String(dados.os.os_numero) }] : null);
  const [erro, setErro] = useState<unknown>(null);
  const [recado, setRecado] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [observacao, setObservacao] = useState('');
  const [ocupado, setOcupado] = useState(false);
  /*
   * O laudo aberto para conferir, antes de assinar.
   *
   * «Poderia abrir um pop-up do pdf de visualização rápida para cada
   *  ensaio, assim fica prático verificar 1 por 1» — Testesolo,
   * 02/09/2026. É o HTML do laudo e não o PDF: abre na hora, e é o mesmo
   * documento que o PDF imprime.
   */
  const [olhando, setOlhando] = useState<
    { titulo: string; corpo: string } | null>(null);

  const carregar = useCallback(async () => {
    try {
      setErro(null);
      setDados(await osParaAssinar(numero));
      setMarcados(new Set());
    } catch (e) { setErro(e); }
  }, [numero]);

  useEffect(() => { void carregar(); }, [carregar]);

  /*
   * Agrupa por AMOSTRA, na ordem de profundidade que a API já devolveu.
   * O agrupamento é da tela porque é ela que desenha o cabeçalho de cada
   * amostra; a ORDEM é do servidor, porque ordenar duas vezes é como as
   * duas ordens divergem.
   */
  const porAmostra = useMemo(() => {
    const g: { amostra: EnsaioParaAssinar; ensaios: EnsaioParaAssinar[] }[] = [];
    for (const e of dados?.ensaios ?? []) {
      const ultimo = g[g.length - 1];
      if (ultimo && ultimo.amostra.amostra_id === e.amostra_id) {
        ultimo.ensaios.push(e);
      } else {
        g.push({ amostra: e, ensaios: [e] });
      }
    }
    return g;
  }, [dados]);

  /*
   * A ordenação DENTRO de cada amostra.
   *
   * «Possibilidade de ordenar as colunas» — Testesolo, 02/09/2026. O
   * estado é um só para todos os blocos: ordenar a amostra de 1,5 m por
   * data e a de 3,0 m por método deixaria a coluna de solo ilegível, que
   * é justamente o que o agrupamento existe para preservar.
   *
   * O padrão abre pelo lançamento MAIS ANTIGO — quem espera há mais
   * tempo em cima, dentro do bloco.
   */
  const { ordem, sentido, trocar } = useOrdenacao('col_lancado',
    { descendentes: ['col_parada', 'col_impedimento'],
      sentidoInicial: 'asc' });

  const emOrdem = useCallback((linhas: EnsaioParaAssinar[]) => {
    const chave: Record<string,
      (e: EnsaioParaAssinar) => string | number | null> = {
      col_ensaio: (e) => e.metodo_codigo,
      col_etapa: (e) => ROTULO_ETAPA[e.etapa] ?? e.etapa,
      col_laboratorista: (e) => e.executado_por_nome,
      col_lancado: (e) => e.lancado_em,
      col_verificacao: (e) => e.data_verificacao,
      col_parada: (e) => e.dias_na_etapa,
      col_impedimento: (e) => e.pendencias_que_travam,
    };
    return ordenarPor(linhas, chave[ordem] ?? chave.col_lancado, sentido);
  }, [ordem, sentido]);

  /** O que EU posso assinar: o que eu mesmo executei não conta (RN-124). */
  const meus = (etapa: EnsaioParaAssinar['etapa']) =>
    (dados?.ensaios ?? []).filter(
      (e) => e.etapa === etapa
        && Number(e.pendencias_que_travam) === 0
        && (e.executado_por !== eu?.usuarioId || eu?.superpoderes === true));

  const alternar = (id: string) => setMarcados((antes) => {
    const novo = new Set(antes);
    if (novo.has(id)) novo.delete(id); else novo.add(id);
    return novo;
  });

  const marcarTodos = (lista: EnsaioParaAssinar[]) => setMarcados((antes) => {
    const novo = new Set(antes);
    const todos = lista.every((e) => novo.has(e.ensaio_id));
    for (const e of lista) {
      if (todos) novo.delete(e.ensaio_id); else novo.add(e.ensaio_id);
    }
    return novo;
  });

  const agir = async (acao: 'verificar' | 'aprovar') => {
    const ids = [...marcados];
    if (!ids.length) return;
    setOcupado(true);
    setRecado(null);
    setOk(null);
    try {
      const corpo = { ensaioIds: ids, observacao: observacao.trim() || undefined };
      const r = acao === 'verificar'
        ? await verificarLote(corpo) : await aprovarLote(corpo);
      const n = 'verificados' in r ? r.verificados : r.aprovados;
      setOk(`${n} ensaio(s) ${acao === 'verificar' ? 'verificado(s)' : 'aprovado(s)'}.`);
      setObservacao('');
      await carregar();
    } catch (e) {
      setRecado(mensagemDoErro(e));
    } finally { setOcupado(false); }
  };

  if (erro) return <ErroDaApi erro={erro} />;
  if (!dados) return <Carregando texto="Abrindo a ordem de serviço" />;

  const { os } = dados;

  /*
   * ── Terminar não é falhar ────────────────────────────────────────
   *
   * «Quando um ensaio é verificado e aprovado, aparece essa mensagem.
   *  Parece um erro para o usuário» — Testesolo, 02/09/2026.
   *
   * A fila ficava vazia porque quem estava nela acabou de ser assinado,
   * e a tela pintava uma tarja vermelha em cima do trabalho concluído.
   * O mesmo estado, dito como o que é.
   */
  if (dados.tudo_assinado) {
    return (
      <div>
        <div className="linha-topo">
          <div className="cresce">
            <h1>OS {os.os_numero}</h1>
            <p className="sub">{os.cliente ?? '—'} · {os.obra_nome ?? '—'}</p>
          </div>
          <button type="button" className="secundario"
                  onClick={() => navegar('/assinatura')}>
            Voltar à fila
          </button>
        </div>

        <Aviso tipo="ok" titulo="Tudo assinado nesta OS">
          Não há ensaio esperando verificação nem aprovação aqui.
          {Number(os.ja_aprovados) > 0 && (
            <> {os.ja_aprovados} ensaio(s) já aprovado(s).</>
          )}
          <div className="sub" style={{ marginTop: '.4rem' }}>
            Ensaios novos desta OS reaparecem aqui assim que os dados
            forem lançados e a ficha for dada por completa.
          </div>
        </Aviso>

        <div className="linha" style={{ marginTop: '.8rem' }}>
          <Link to={`/os/${encodeURIComponent(os.os_numero)}`}
                className="botao secundario">
            Abrir a ficha da OS
          </Link>
          <Link to="/assinatura" className="botao">
            Ver a fila do laboratório
          </Link>
        </div>
      </div>
    );
  }
  const podeVerificar = pode('ensaio.verificar');
  const podeAprovar = pode('ensaio.aprovar');

  return (
    <div>
      <div className="linha-topo">
        <div className="cresce">
          <h1>OS {os.os_numero}</h1>
          <p className="sub">
            {os.cliente ?? '—'} · {os.obra_nome ?? '—'} ·{' '}
            {os.amostras} amostra(s), {os.ensaios} ensaio(s) esperando
          </p>
        </div>
        <button type="button" className="secundario"
                onClick={() => navegar('/assinatura')}>
          Voltar à fila
        </button>
      </div>

      {os.ainda_no_laboratorio > 0 && (
        <Aviso tipo="info" titulo="A OS não está inteira">
          {os.ainda_no_laboratorio} ensaio(s) desta OS ainda estão no
          laboratório. Dá para assinar os que já chegaram — este aviso
          existe para a decisão ser tomada sabendo, e não para impedir.
        </Aviso>
      )}

      {os.travados > 0 && (
        <Aviso tipo="atencao" titulo="Pendências que travam">
          {os.travados} ensaio(s) têm pendência que impede a assinatura —
          calibração vencida, competência, cálculo não reconhecido. Eles
          aparecem marcados abaixo e não entram na seleção; abra a ficha
          para resolver ou justificar.
        </Aviso>
      )}

      {recado && <Aviso tipo="erro">{recado}</Aviso>}
      {ok && <Aviso tipo="ok">{ok}</Aviso>}

      {porAmostra.map(({ amostra, ensaios }) => (
        <section key={amostra.amostra_id} className="bloco">
          <h2>
            {/* 303 (RN-248): dentro da OS, o nome do cliente e o código. */}
            <NomeDaAmostra amostra={daApi(amostra)} emLinha forte={false} />
            {prof(amostra.profundidade_inicial, amostra.profundidade_final)
              && (
                <span className="sub">
                  {' '}·{' '}
                  {prof(amostra.profundidade_inicial,
                    amostra.profundidade_final)}
                </span>
              )}
            {amostra.camada && <span className="sub"> · {amostra.camada}</span>}
            {amostra.ponto_investigacao && (
              <span className="sub"> · {amostra.ponto_investigacao}</span>
            )}
          </h2>

          <div className="rola"><table className="dados">
            <thead>
              <tr>
                <th />
                <ColunaOrdenavel campo="col_ensaio" texto="Ensaio"
                  ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                <ColunaOrdenavel campo="col_etapa" texto="Etapa"
                  ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                <ColunaOrdenavel campo="col_laboratorista"
                  texto="Laboratorista"
                  ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                {/*
                  * «Lançado há X dias» — o pedido da Testesolo, e um
                  * relógio DIFERENTE de «parado há». Aquele reinicia no
                  * carimbo mais recente da escada, então um ensaio
                  * conferido ontem está «parado há 1 d»; este conta
                  * desde o lançamento do dado e continua em 9. As duas
                  * frases são verdadeiras ao mesmo tempo, e é a segunda
                  * que diz que o laudo está atrasado.
                  */}
                <ColunaOrdenavel campo="col_lancado" texto="Lançado"
                  ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                <ColunaOrdenavel campo="col_verificacao" texto="Verificação"
                  ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                <ColunaOrdenavel campo="col_parada" texto="Parado há"
                  classe="num"
                  ordem={ordem} sentido={sentido} aoTrocar={trocar} />
                {/*
                  * O impedimento também ordena. «Sempre coloque setas
                  * para ordenar [...] em cada coluna» — e aqui ela é
                  * útil de verdade: traz para cima o que precisa ser
                  * resolvido antes de a assinatura ser possível.
                  */}
                <ColunaOrdenavel campo="col_impedimento"
                  texto="Impedimento"
                  ordem={ordem} sentido={sentido} aoTrocar={trocar} />
              </tr>
            </thead>
            <tbody>
              {emOrdem(ensaios).map((e) => {
                /* RN-265 (0268): com os superpoderes, o próprio também se assina. */
                const meu = e.executado_por === eu?.usuarioId && !eu?.superpoderes;
                const travado = Number(e.pendencias_que_travam) > 0;
                return (
                  <tr key={e.ensaio_id}
                      className={travado ? 'descartada' : undefined}>
                    <td>
                      <input
                        type="checkbox"
                        checked={marcados.has(e.ensaio_id)}
                        disabled={travado || meu}
                        aria-label={`Selecionar ${e.metodo_codigo}`}
                        onChange={() => alternar(e.ensaio_id)}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="como-link"
                        title="Abrir o laudo para conferir antes de assinar"
                        onClick={async () => {
                          setRecado(null);
                          try {
                            setOlhando({
                              titulo: `${e.metodo_codigo} · ${amostra.amostra_codigo}`,
                              corpo: await laudoParaConferir(e.ensaio_id),
                            });
                          } catch (erroLaudo) {
                            setRecado(mensagemDoErro(erroLaudo));
                          }
                        }}
                      >
                        {e.metodo_codigo}
                      </button>
                      <small className="det linha-de-baixo">
                        {e.metodo_nome}
                      </small>
                      {/*
                        * A ficha continua a um clique: conferir é olhar o
                        * laudo; RESOLVER uma pendência é ir para a ficha,
                        * e são coisas diferentes.
                        */}
                      <Link to={`/ensaio/${e.ensaio_id}/situacao`}
                            className="det linha-de-baixo">
                        abrir a ficha
                      </Link>
                    </td>
                    <td>
                      <Selo tom={TOM_ETAPA[e.etapa] ?? 'neutro'}>
                        {ROTULO_ETAPA[e.etapa] ?? e.etapa}
                      </Selo>
                    </td>
                    <td>
                      {e.executado_por_nome ?? '—'}
                      <small className="det linha-de-baixo">
                        {dia(e.data_execucao)}
                      </small>
                    </td>
                    <td>
                      {haDias(e.dias_desde_lancamento) === '—' ? '—' : (
                        tomDaEspera(e.dias_desde_lancamento)
                          ? (
                            <Selo tom={tomDaEspera(e.dias_desde_lancamento)!}
                              titulo={`Lançado em ${dia(e.lancado_em)}`}>
                              {haDias(e.dias_desde_lancamento)}
                            </Selo>
                          )
                          : haDias(e.dias_desde_lancamento)
                      )}
                      <small className="det linha-de-baixo">
                        {dia(e.lancado_em)}
                      </small>
                    </td>
                    <td>
                      {e.verificado_por_nome ?? <span className="nada">—</span>}
                      {e.data_verificacao && (
                        <small className="det linha-de-baixo">
                          {dia(e.data_verificacao)}
                        </small>
                      )}
                    </td>
                    <td className="num">
                      {e.dias_na_etapa == null ? '—' : `${e.dias_na_etapa} d`}
                    </td>
                    <td>
                      {travado && (
                        <span className="erro-fila">
                          {e.pendencias_que_travam} pendência(s)
                        </span>
                      )}
                      {/*
                        * O §15: quem executou não confere nem aprova o
                        * próprio trabalho. Dizer isso NA LINHA é melhor
                        * do que deixar a caixa desmarcável sem explicar
                        * — senão parece defeito.
                        */}
                      {meu && !travado && (
                        <span className="nada">você executou</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>

          <button type="button" className="secundario"
                  onClick={() => marcarTodos(ensaios.filter(
                    (e) => Number(e.pendencias_que_travam) === 0
                      && (e.executado_por !== eu?.usuarioId || eu?.superpoderes === true)))}>
            Marcar / desmarcar esta amostra
          </button>
        </section>
      ))}

      <section className="bloco">
        <h2>Assinar o que está marcado</h2>
        <p className="sub">
          {marcados.size} selecionado(s). O lote é tudo-ou-nada: se um
          deles não puder, nenhum é assinado e a mensagem diz qual e por
          quê.
        </p>

        <Campo
          rotulo="Observação (opcional)"
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          dica="Vai para a trilha de auditoria junto com a assinatura."
        />

        <div className="acoes">
          {podeVerificar && (
            <button type="button" disabled={ocupado || marcados.size === 0}
                    onClick={() => void agir('verificar')}>
              Verificar ({marcados.size})
            </button>
          )}
          {podeAprovar && (
            <button type="button" disabled={ocupado || marcados.size === 0}
                    onClick={() => void agir('aprovar')}>
              Aprovar ({marcados.size})
            </button>
          )}
          <button type="button" className="secundario"
                  onClick={() => setMarcados(new Set(
                    meus('aguardando_verificacao')
                      .concat(meus('aguardando_aprovacao'))
                      .map((e) => e.ensaio_id)))}>
            Marcar tudo que eu posso assinar
          </button>
        </div>

        {olhando && (
          <JanelaDocumento
            titulo={`Laudo · ${olhando.titulo}`}
            corpo={olhando.corpo}
            aoFechar={() => setOlhando(null)}
          />
        )}

        {!podeVerificar && !podeAprovar && (
          <Aviso tipo="info">
            O seu perfil enxerga esta fila, mas não assina. Verificar exige
            «ensaio.verificar»; aprovar exige «ensaio.aprovar».
          </Aviso>
        )}
      </section>
    </div>
  );
}
